'use client'

import { useState } from 'react'
import { Link, useRouter } from '@/shared/navigation'
import { ClientRedirect } from '@/shared/components/ClientRedirect'
import { loginKitchenPin } from '@/features/auth/api/pinLogin'
import { getKitchenDevice, hasKitchenDevice, clearKitchenDevice } from '@/shared/auth/deviceStorage'
import { clearDeviceUid } from '@/shared/auth/deviceUid'
import { SWITCH_DEVICE_HINT } from '@/features/auth/deviceSwitchCopy'
import { clearKitchenSession, hasKitchenSession, setKitchenSession } from '@/shared/auth/sessionStorage'
import { useClientHydrated } from '@/shared/hooks/useClientHydrated'

type Props = {
  platform: 'kitchen' | 'delman'
  redirectTo: string
  requireDevice?: boolean
}

export function PinLoginPage({ platform, redirectTo, requireDevice }: Props) {
  const router = useRouter()
  const hydrated = useClientHydrated()
  const base = platform === 'kitchen' ? '/kitchen' : '/delman'
  const [pin, setPin] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [shakeKey, setShakeKey] = useState(0)

  if (hydrated && requireDevice && platform === 'kitchen' && !hasKitchenDevice()) {
    return <ClientRedirect href="/kitchen/activate" />
  }
  if (hydrated && platform === 'kitchen' && hasKitchenSession()) {
    return <ClientRedirect href={redirectTo} />
  }

  const device = hydrated && platform === 'kitchen' ? getKitchenDevice() : null
  const maxDigits = 6
  const canSubmit = pin.length === maxDigits && !isSubmitting

  function addDigit(digit: string) {
    if (isSubmitting) return
    setPin((prev) => (prev.length >= maxDigits ? prev : `${prev}${digit}`))
    setError(null)
  }

  function removeDigit() {
    if (isSubmitting) return
    setPin((prev) => prev.slice(0, -1))
    setError(null)
  }

  async function submitPin() {
    if (!device || !canSubmit) return
    setIsSubmitting(true)
    setError(null)
    try {
      const session = await loginKitchenPin({
        device_id: device.deviceId,
        pin,
      })
      setKitchenSession(session)
      router.replace(redirectTo)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login gagal. Coba lagi.')
      setPin('')
      setShakeKey((prev) => prev + 1)
    } finally {
      setIsSubmitting(false)
    }
  }

  const keypadDigits = ['1', '2', '3', '4', '5', '6', '7', '8', '9']
  return (
    <main className="w-full max-w-[480px]">
      <div className="bg-surface-card rounded-xl border border-outline-variant shadow-sm overflow-hidden flex flex-col">
        <div className="bg-surface pt-8 pb-6 px-gutter-tablet sm:px-gutter-desktop flex flex-col items-center border-b border-surface-variant">
          <div className="w-16 h-16 rounded-full bg-primary-container flex items-center justify-center mb-4 text-on-primary-container shadow-sm border border-outline-variant/30">
            <span className="material-symbols-outlined text-[32px] icon-fill">pin</span>
          </div>
          {device && (
            <p className="text-label-md text-on-surface-variant mb-2">
              {device.kitchenName} · {device.deviceName}
            </p>
          )}
          <h1 className="text-headline-md text-primary mb-1 text-center">Login PIN</h1>
          <p className="text-body-sm text-on-surface-variant text-center">Masukkan PIN 6 digit untuk akses dapur.</p>
        </div>

        <div className="p-gutter-tablet sm:p-gutter-desktop flex flex-col gap-5">
          <div key={shakeKey} className="flex justify-center gap-2 pin-shake" aria-label="PIN indicator">
            {Array.from({ length: 6 }).map((_, i) => (
              <div
                key={i}
                className={`w-3 h-3 rounded-full border border-outline ${
                  i < pin.length ? 'bg-primary border-primary' : 'bg-surface'
                }`}
              />
            ))}
          </div>

          <div className={`grid grid-cols-3 gap-3 transition-opacity ${isSubmitting ? 'opacity-60' : 'opacity-100'}`}>
            {keypadDigits.map((digit) => (
              <button
                key={digit}
                type="button"
                disabled={isSubmitting}
                onClick={() => addDigit(digit)}
                className="pin-key"
                aria-label={`Angka ${digit}`}
              >
                {digit}
              </button>
            ))}
            <button
              type="button"
              disabled={isSubmitting}
              onClick={removeDigit}
              className="pin-key"
              aria-label="Hapus digit terakhir"
            >
              <span className="material-symbols-outlined text-xl">backspace</span>
            </button>
            <button
              type="button"
              disabled={isSubmitting}
              onClick={() => addDigit('0')}
              className="pin-key"
              aria-label="Angka 0"
            >
              0
            </button>
            <div />
          </div>

          {error && (
            <div role="alert" className="alert-error">
              <span className="material-symbols-outlined text-base shrink-0">error</span>
              <span>{error}</span>
            </div>
          )}
          {!error && pin.length < maxDigits && (
            <p className="text-body-sm text-on-surface-variant text-center">Isi 6 digit PIN untuk lanjut.</p>
          )}

          <button
            type="button"
            disabled={!canSubmit}
            onClick={submitPin}
            className="btn-cta btn-tactile"
          >
            {isSubmitting ? 'Memverifikasi…' : 'Masuk'}
          </button>
        </div>

        <div className="bg-surface-container py-4 px-gutter-tablet sm:px-gutter-desktop text-center border-t border-surface-variant space-y-2">
          <Link
            href={`${base}/activate`}
            onClick={() => {
              if (platform === 'kitchen') {
                clearKitchenSession()
                clearKitchenDevice()
                clearDeviceUid()
              }
            }}
            className="text-label-md text-primary inline-flex items-center gap-2"
          >
            <span className="material-symbols-outlined text-sm">sync_alt</span>
            Ganti device
          </Link>
          <p className="text-label-md text-on-surface-variant px-2">{SWITCH_DEVICE_HINT}</p>
        </div>
      </div>
    </main>
  )
}
